import { useAppSelector } from 'hooks/useReduxHooks';
import { User } from 'interfaces/user';
import { FC, JSX } from 'react';
import { Navigate } from 'react-router-dom';
import { selectUser } from 'store/slices/auth';

type ProtectedRouteProps = {
  children: JSX.Element;
  roles: string[];
};

/**
 * @description Role-based authorization guard component that restricts access based on user roles.
 * Redirects users without proper role permissions to the home page.
 *
 * @SADtrace SAD-003-1, SAD-003-1.2
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @param {ProtectedRouteProps} props - Component props
 * @param {JSX.Element} props.children - Child components to render if authorized
 * @param {string[]} props.roles - Array of allowed roles for this route
 *
 * @returns {JSX.Element} Either the children or a redirect to home page
 */
const ProtectedRoute: FC<ProtectedRouteProps> = ({ children, roles }) => {
  const user: User | null = useAppSelector(selectUser);

  if (!user?.role || !roles.includes(user.role)) {
    // If user role is not authorized for this route, redirect to unauthorized page
    return <Navigate to="/" />;
  }

  return children;
};

export default ProtectedRoute;
